import axios from "axios";
import router from "../../router";
import {eventBus} from "../../main";

export const SCROLL_DIRECTION_TOP = 'top';
export const SCROLL_DIRECTION_BOTTOM = 'bottom';
export const EMIT_ROOM_SCROLL_TO_MESSAGE = 'room_scroll_to_message';

const msgLimit = 40;

const stateBase = () => ({
  loadingFirst: true,
  loadingMessage: false,

  scrollDirection: SCROLL_DIRECTION_TOP,
  scrollTopFinished: false,
  scrollBottomFinished: false,

  messageMax: 0,
  messageIdSearch: '',
  messages: [],
  messagesRetry: {},
});

export const state = stateBase;

export const mutations = {
  reset(store) {
    Object.assign(store, stateBase());
  },
  setLoadingFirst(store, loading) {
    store.loadingFirst = loading;
  },
  setLoadingMessage(store, loading) {
    store.loadingMessage = loading;
  },
  SET_SCROLL_DIRECTION(store, scrollDirection) {
    store.scrollDirection = scrollDirection;
  },
  SET_SCROLL_TOP_FINISHED(store, is) {
    store.scrollTopFinished = is;
  },
  SET_SCROLL_BOTTOM_FINISHED(store, is) {
    store.scrollBottomFinished = is;
  },
  setMessageMax(store, max) {
    store.messageMax = max;
  },
  ADD_MESSAGES(store, {messages, ...data}) {
    addMessage(store, data, messages);
  },
  ADD_MESSAGE(store, {message, ...data}) {
    addMessage(store, data, [message]);
  },
  SET_MESSAGE_ID_SEARCH(store, messageIdSearch) {
    store.messageIdSearch = messageIdSearch || '';
  },
  UPDATE_MESSAGE_STATUS(store, {message_id, status} = {}) {
    store.messages = store.messages.map(m => (message_id === m.id) ? {...m, status} : m);
  },
  DELETE_MESSAGE(store, {message_id, meta_id} = {}) {
    store.messages = store.messages.filter(m => !(
      (!!message_id && message_id === m.id) ||
      (!!meta_id && meta_id === m.meta_id)
    ));
  },
  ERROR_MESSAGES(store, {meta_id, message_id, error = 'undefined'}) {
    store.messages = store.messages.map(m => (
      (!!message_id && m.id === message_id) ||
      (!!meta_id && m.meta_id === meta_id)
    ) ? {...m, is_sending: false, error} : m);
  },
};

export const getters = {
  isScrollDirectionTop: state => state.scrollDirection === SCROLL_DIRECTION_TOP,
  isScrollDirectionBottom: state => state.scrollDirection === SCROLL_DIRECTION_BOTTOM,
};

export const actions = {
  async goto({commit, dispatch, state, rootState}, {chat_id, message_id}) {
    //  console.info('room/goto | {chat_id = ', chat_id, ', message_id = ', message_id, '}');

    commit('rooms/SET_SHOW_ROOM', undefined, {root: true});
    commit('rooms/SET_SELECTED_ROOM_ID', chat_id, {root: true});

    const path = '/chat/' + chat_id + (message_id ? '#' + message_id : '');
    router.push(path).catch(error => {
      if (error.name !== "NavigationDuplicated") {
        throw error;
      }
    });

    if (!rootState.rooms.rooms[chat_id]) {
      await dispatch('rooms/one', {id: chat_id}, {root: true});
    }

    const find = message_id && state.messages.find(({id}) => message_id === id);
    // console.info('room/goto | find = ', find);

    !find && commit('reset');
    commit('SET_MESSAGE_ID_SEARCH', message_id);
    !find && await dispatch('fetchMessage');

    eventBus.$emit(EMIT_ROOM_SCROLL_TO_MESSAGE, {message_id});
  },
  async fetchMessageOnNewMessage({commit, state, rootState}) {
    const {
      selectedRoomId: chat_id,
    } = rootState.rooms;

    const {
      loadingMessage,
      messageIdSearch,
      messageMax,
    } = state;

    if (loadingMessage) return;

    const payload = {
      chat_id,
      offset: messageMax - msgLimit - 1,
      count: msgLimit,
    };

    commit('SET_SCROLL_BOTTOM_FINISHED', true);
    await new Promise(resolve => setTimeout(resolve, 300));

    return axios
      .get(`/chats/api/v1/messages/${payload.chat_id}/history`, {
        params: {offset: payload.offset, count: payload.count},
      })
      .then(messagesHistoryThen(payload.chat_id, messageIdSearch))
      .then(({messages, total_entries_size}) => {
        messages.splice(-1);
        commit('setMessageMax', total_entries_size);
        commit('ADD_MESSAGES', {messages});
      })
      .catch(e => {
        console.error('chat.room.fetchMessageOnNewMessage | e = ', e);
        commit('setLoadingMessage', e);
        throw e;
      });
  },
  async fetchMessage({dispatch, commit, state, getters, rootState}, {direction = SCROLL_DIRECTION_BOTTOM} = {}) {
    commit('SET_SCROLL_DIRECTION', direction);

    const {
      selectedRoomId: chat_id,
    } = rootState.rooms;

    const {
      loadingFirst,
      loadingMessage,
      finishedTop,
      finishedBottom,
      messageIdSearch,
      messages,
    } = state;

    const {
      isScrollDirectionTop,
      isScrollDirectionBottom,
    } = getters;

    if (isScrollDirectionTop && finishedTop) return;
    if (isScrollDirectionBottom && finishedBottom) return;

    if (loadingMessage) return;

    const payload = {
      chat_id,
      offset: 0,
      count: msgLimit,
    };

    if (loadingFirst) {
      if (messageIdSearch.length) {
        payload.start_message_id = messageIdSearch;
        payload.offset = -msgLimit / 2;
      }
    } else if (messages.length) {
      if (isScrollDirectionTop) {
        payload.start_message_id = messages[0].id;
        payload.offset = -msgLimit;
      } else if (isScrollDirectionBottom) {
        payload.start_message_id = messages
          .slice(0).reverse()
          .find(({id}) => typeof id === 'string').id;
      }
    } else {
      isScrollDirectionTop && commit('SET_SCROLL_TOP_FINISHED', true);
      isScrollDirectionBottom && commit('SET_SCROLL_BOTTOM_FINISHED', true);
      return;
    }

    try {
      commit('setLoadingMessage', true);

      const {messages: messagesNew, total_entries_size} = await axios
        .get(`/chats/api/v1/messages/${payload.chat_id}/history`, {
          params: {start_message_id: payload.start_message_id, offset: payload.offset, count: payload.count},
        })
        .then(messagesHistoryThen(payload.chat_id, messageIdSearch))
        .catch(e => {
          console.error('chat.room.fetchMessage | e = ', e);
          commit('setLoadingMessage', e);
          throw e;
        });

      commit('setMessageMax', total_entries_size);

      if (
        isScrollDirectionTop &&
        !messagesNew.length
      ) {
        commit('SET_SCROLL_TOP_FINISHED', true);
        return;
      }
      if (
        isScrollDirectionBottom &&
        (
          !messagesNew.length ||
          (messagesNew.length && payload.start_message_id === messagesNew.slice(-1).pop().id)
        )
      ) {
        commit('SET_SCROLL_BOTTOM_FINISHED', true);
        return;
      }

      if (loadingFirst) {
        if (messageIdSearch.length) {
          const messageIdSearchIndex = messagesNew.findIndex(({id}) => id === messageIdSearch);

          if (messageIdSearchIndex < payload.count / 2) {
            commit('SET_SCROLL_TOP_FINISHED', true);
          }

          if (messagesNew.length - messageIdSearchIndex < payload.count / 2) {
            commit('SET_SCROLL_BOTTOM_FINISHED', true);
          }
        } else {
          commit('SET_SCROLL_BOTTOM_FINISHED', true);
        }
      }

      commit('ADD_MESSAGES', {messages: messagesNew});

    } catch (e) {
      console.error('fetchMessage | e = ', e);
    } finally {
      commit('setLoadingMessage', false);
      loadingFirst && commit('setLoadingFirst', false);
    }
  },
};

function addMessage(store, {
  meta_id,
  is_sending = false,
  retry = undefined,
}, messagesNew) {
  messagesNew = messagesNew.map(m => {
    const created_at = Date.parse(m.created_at);
    return {
      ...m,
      created_at: Date.parse(m.created_at),
      created_at_json: new Date(created_at).toJSON(),
    };
  });

  let messagesStoreNew = [...store.messages];

  messagesNew.forEach((message, index) => {
    let messagesFind = false;

    const messageNew = {...message, meta_id, is_sending, error: false};

    messagesStoreNew = messagesStoreNew.map(messageOld => {
      if (
        messageOld.id === message.id ||
        (!!meta_id && messageOld.meta_id === meta_id)
      ) {
        messagesFind = true;
        if (!!meta_id && messageOld.meta_id === meta_id) {
          messageNew.index = messageOld.index;
        }
        return messageNew;
      } else {
        return messageOld;
      }
    });

    if (messagesFind === false) {
      if (!messageNew.index) {
        messageNew.index = store.messageMax;
        store.messageMax++;
      }
      messagesStoreNew.push(messageNew);
    }
  });

  messagesStoreNew = messagesStoreNew.reduce((ret, msg) => (ret[msg.id] = msg, ret), {});
  messagesStoreNew = Object.values(messagesStoreNew);
  messagesStoreNew = messagesStoreNew.sort(
    (a, b) => a.created_at === b.created_at ? 0 : a.created_at > b.created_at ? 1 : -1,
  );

  let firstOnToday = false;
  let today = new Date().toJSON().slice(0, 10);
  messagesStoreNew = messagesStoreNew.map((message, index) => {
    const _firstOnToday = (!firstOnToday && today === message.created_at_json.slice(0, 10));
    if (_firstOnToday) firstOnToday = true;
    return {...message, _firstOnToday};
  });

  messagesStoreNew = messagesStoreNew.map((message, index) => {
    return {...message, __id: message.id || message.meta_id};
  });
  store.messages = messagesStoreNew;

  if (retry) {
    store.messagesRetry[meta_id] = retry;
  }
}

const messagesHistoryThen = (chat_id, midSearch) => ({data: {body, meta: {offset, total_entries_size}}}) => ({
  messages: (body || []).map((message, index) => ({
    ...message,
    chat_id,
    _is_message_search: midSearch === message.id,
    index: offset + index,
  })),
  total_entries_size,
});

